'use client';

import type { PortfolioItemData, UpdateSupplierProfileData } from '@/lib/api/suppliers';
import { suppliersApi } from '@/lib/api/suppliers';
import type { PortfolioItem, Supplier, SupplierProfile } from '@/lib/types/supplier.types';
import { useCallback, useEffect, useState } from 'react';

export function useSuppliers(params?: import('@/lib/types/common.types').QueryParams) {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchSuppliers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await suppliersApi.getAll(params);
        setSuppliers(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchSuppliers();
  }, [params]);

  return { suppliers, isLoading, error };
}

export function useSupplier(supplierId: string | null) {
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchSupplier = async () => {
      if (!supplierId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const data = await suppliersApi.getById(supplierId);
        setSupplier(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchSupplier();
  }, [supplierId]);

  return {
    supplier,
    isLoading,
    error,
  };
}

export function useSupplierProfile() {
  const [profile, setProfile] = useState<SupplierProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchProfile = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await suppliersApi.getMyProfile();
      setProfile(data);
    } catch (err: any) {
      // If profile doesn't exist (404), treat it as normal (not an error)
      // This allows the form to work for new suppliers who haven't created their profile yet
      if (err?.response?.status === 404 || err?.status === 404) {
        setProfile(null);
        setError(null); // Don't set error for 404
      } else {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const updateProfile = async (data: UpdateSupplierProfileData) => {
    setIsLoading(true);
    setError(null);
    try {
      const updated = await suppliersApi.updateProfile(data);
      setProfile(updated);
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    profile,
    isLoading,
    error,
    refetch: fetchProfile,
    updateProfile,
  };
}

export function usePortfolio() {
  const [items, setItems] = useState<PortfolioItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchPortfolio = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const profile = await suppliersApi.getMyProfile();
      setItems(profile.portfolio || []);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPortfolio();
  }, [fetchPortfolio]);

  const addItem = async (itemData: PortfolioItemData) => {
    setIsLoading(true);
    setError(null);
    try {
      const newItem = await suppliersApi.addPortfolioItem(itemData);
      setItems((prev) => [...prev, newItem]);
      return newItem;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateItem = async (id: string, itemData: Partial<PortfolioItemData>) => {
    setIsLoading(true);
    setError(null);
    try {
      const updated = await suppliersApi.updatePortfolioItem(id, itemData);
      setItems((prev) => prev.map((item) => (item.id === id ? updated : item)));
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteItem = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await suppliersApi.deletePortfolioItem(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    items,
    isLoading,
    error,
    refetch: fetchPortfolio,
    addItem,
    updateItem,
    deleteItem,
  };
}

export function useSupplierStatistics() {
  const [stats, setStats] = useState<{
    totalOffers: number;
    acceptedOffers: number;
    pendingOffers: number;
    rejectedOffers: number;
    withdrawnOffers: number;
    totalProjects: number;
    completedProjects: number;
    activeProjects: number;
    pendingProjects: number;
    cancelledProjects: number;
    totalEarnings: number;
    averageRating: number;
    totalReviews: number;
    totalDisputes: number;
    pendingDisputes: number;
    responseTime: number;
    onTimeDelivery: number;
    portfolioItems: number;
    certifications: number;
    isVerified: boolean;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // First get profile to get supplier ID
        const profileData = await suppliersApi.getMyProfile();
        const supplierId = profileData.id;

        // Fetch all statistics in parallel
        const [basicStats, performance, myOffers, myProjects, reviewStats, disputeStats] =
          await Promise.allSettled([
            suppliersApi.getStatistics(),
            suppliersApi.getPerformance(),
            import('@/lib/api/offers').then((m) => m.offersApi.getMyOffers()),
            import('@/lib/api/projects').then((m) => m.projectsApi.getAll()),
            import('@/lib/api/reviews').then((m) => m.reviewsApi.getSupplierStatistics(supplierId)),
            import('@/lib/api/disputes').then((m) => m.disputesApi.getAll()),
          ]);

        const statsData = basicStats.status === 'fulfilled' ? basicStats.value : {};
        const perfData = performance.status === 'fulfilled' ? performance.value : {};
        const offersData = myOffers.status === 'fulfilled' ? myOffers.value : [];
        const projectsData = myProjects.status === 'fulfilled' ? myProjects.value : [];
        const reviewsData = reviewStats.status === 'fulfilled' ? reviewStats.value : null;
        const disputesData = disputeStats.status === 'fulfilled' ? disputeStats.value : [];

        // Calculate offers statistics
        const pendingOffers = offersData.filter(
          (o: { status: string }) => o.status === 'pending'
        ).length;
        const rejectedOffers = offersData.filter(
          (o: { status: string }) => o.status === 'rejected'
        ).length;
        const withdrawnOffers = offersData.filter(
          (o: { status: string }) => o.status === 'withdrawn'
        ).length;

        // Calculate projects statistics
        const activeProjects = projectsData.filter(
          (p: { status: string }) => p.status === 'in_progress' || p.status === 'awaiting_payment'
        ).length;
        const pendingProjects = projectsData.filter(
          (p: { status: string }) => p.status === 'new'
        ).length;
        const cancelledProjects = projectsData.filter(
          (p: { status: string }) => p.status === 'cancelled'
        ).length;

        // Calculate disputes statistics
        const totalDisputes = disputesData.length;
        const pendingDisputes = disputesData.filter(
          (d: { status: string }) => d.status === 'open' || d.status === 'pending'
        ).length;

        setStats({
          totalOffers: statsData.totalOffers || offersData.length,
          acceptedOffers:
            statsData.acceptedOffers ||
            offersData.filter((o: { status: string }) => o.status === 'accepted').length,
          pendingOffers,
          rejectedOffers,
          withdrawnOffers,
          totalProjects: statsData.totalProjects || projectsData.length,
          completedProjects:
            statsData.completedProjects ||
            projectsData.filter((p: { status: string }) => p.status === 'completed').length,
          activeProjects,
          pendingProjects,
          cancelledProjects,
          totalEarnings: statsData.totalEarnings || 0,
          averageRating: statsData.averageRating || reviewsData?.averageRating || 0,
          totalReviews: reviewsData?.totalReviews || 0,
          totalDisputes,
          pendingDisputes,
          responseTime: (perfData as any)?.responseTime || profileData?.responseTime || 0,
          onTimeDelivery: (perfData as any)?.onTimeDelivery || profileData?.onTimeDelivery || 0,
          portfolioItems: profileData?.portfolio?.length || 0,
          certifications: profileData?.certifications?.length || 0,
          isVerified: profileData?.isVerified || false,
        });
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  return { stats, isLoading, error };
}
